import type { FetchUserPreferencesEpicApi } from 'epic/userPreferences/fetchUserPreferencesEpic';
import type { SaveUserPreferencesEpicApi } from 'epic/userPreferences/saveUserPreferencesEpic';
import type { FetchUserPreferencesAvailableCurrenciesApi } from 'epic/userPreferences/updateUserPreferencesAvailableCurrenciesEpic';
import type { ProductVariation } from 'models/productsReferential';
import type { UnderlyingCodeType } from 'models/underlying/base';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { ProductCodeType } from 'state/secondaryMarket/productSearch';
import type {
  AvailableProduct,
  BatchWorkspaceVersion,
  DefaultFieldValues,
  DisplayPrecisionValues,
  EssentialExpandedBlocks,
  LandingPage,
  Notifications,
  ProductLayoutPreferences,
  QuoteWorkspaceVersion,
  UserPreferences,
} from 'state/userPreferences/userPreferences.model';
import type { IHttpBody, IHttpClient } from 'utils/http/httpClient.model';

export type UserPreferencesApi = FetchUserPreferencesEpicApi &
  SaveUserPreferencesEpicApi &
  UpdateUserPreferencesProductLayoutApi &
  FetchUserPreferencesAvailableCurrenciesApi;

export function userPreferencesApiConnector(http$: IHttpClient): UserPreferencesApi {
  return {
    fetchUserPreferences(): Observable<UserPreferences> {
      return http$
        .getJSON<UserPreferencesData>({
          url: 'api/v1/user-preferences',
        })
        .pipe(
          map(
            ({ payload }) =>
              ({
                ...payload.preferences,
                availableSpProducts: payload.availableSpProducts,
                availableCurrencies: payload.availableCurrencies,
                availableDiscoverPageRegions: payload.availableDiscoverPageRegions,
                productCodeType: payload.preferences.productSearchCodeType,
                closeTabWhenLiveTradeCpOrderIsPlacedActivated:
                  payload.preferences.closeTabWhenLiveTradeCpOrderIsPlacedActivated,
              }) as UserPreferences,
          ),
        );
    },
    saveUserPreferences(userPreferences) {
      return http$
        .post({
          url: 'api/v1/user-preferences',
          body: {
            isDarkMode: userPreferences.isDarkMode,
            landingPage: userPreferences.landingPage,
            quoteWorkspaceVersion: userPreferences.quoteWorkspaceVersion,
            batchWorkspaceVersion: userPreferences.batchWorkspaceVersion,
            languageIsoCode: userPreferences.languageIsoCode,
            defaultFieldValues: userPreferences.defaultFieldValues,
            precision: userPreferences.precision ?? {},
            underlyingCodeType: userPreferences.underlyingCodeType,
            tabsOrder: userPreferences.tabsOrder,
            discoverPageRegion: userPreferences.discoverPageRegion,
            productSearchCodeType: userPreferences.productCodeType,
            closeTabWhenLiveTradeCpOrderIsPlacedActivated:
              userPreferences.closeTabWhenLiveTradeCpOrderIsPlacedActivated,
            pinnedProductVariations: userPreferences.pinnedProductVariations,
            essentialPinnedProductVariations: userPreferences.essentialPinnedProductVariations,
            notifications: userPreferences.notifications,
            showBatchShortcutCheatsheet: userPreferences.showBatchShortcutCheatsheet,
            essentialExpandedBlocks: userPreferences.essentialExpandedBlocks,
            productLayoutPreferences: userPreferences.productLayoutPreferences,
          } as Preferences,
        })
        .pipe(map(({ payload }) => payload));
    },
    updateUserPreferenceProductLayout(productLayout) {
      return http$
        .put({
          url: 'api/v1/user-preferences/product-layout',
          body: productLayout as unknown as IHttpBody,
        })
        .pipe(map(({ payload }) => payload));
    },
    fetchAvailableCurrencies(productSubtype) {
      return http$
        .getJSON<{ availableCurrencies: ReadonlyArray<string | null> }>({
          url: `api/v1/user-preferences/${productSubtype}/available-currencies`,
        })
        .pipe(map(({ payload }) => payload.availableCurrencies));
    },
  };
}

export type UserPreferencesData = Readonly<{
  preferences: Preferences;
  availableSpProducts: ReadonlyArray<AvailableProduct>;
  availableCurrencies: ReadonlyArray<string>;
  availableDiscoverPageRegions: ReadonlyArray<string>;
  availableBatchScreenProducts: ReadonlyArray<AvailableProduct>;
}>;

interface Preferences {
  languageIsoCode: string;
  isDarkMode: boolean;
  landingPage?: LandingPage;
  discoverPageRegion?: string;
  quoteWorkspaceVersion?: QuoteWorkspaceVersion;
  batchWorkspaceVersion?: BatchWorkspaceVersion;
  defaultFieldValues: DefaultFieldValues;
  precision?: DisplayPrecisionValues;
  underlyingCodeType: UnderlyingCodeType;
  tabsOrder: {
    spmVisible: string[];
    spmHidden: string[];
  };
  productSearchCodeType: ProductCodeType;
  closeTabWhenLiveTradeCpOrderIsPlacedActivated: boolean;
  pinnedProductVariations: ProductVariation[];
  essentialPinnedProductVariations: ProductVariation[];
  notifications: Notifications;
  showBatchShortcutCheatsheet?: boolean;
  essentialExpandedBlocks?: EssentialExpandedBlocks;
  productLayoutPreferences?: ProductLayoutPreferences;
}

export interface UpdateUserPreferencesProductLayoutApi {
  updateUserPreferenceProductLayout(productLayoutPrefs: ProductLayoutPreferences | null): Observable<void>;
}
